import { Formik } from "formik";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { Creators as ReportActions } from "../../../store/ducks/report";
import { Creators as FinancialPaymentMethodActions } from "../../../store/ducks/financialPaymentMethod";
import { Creators as FinancialTaxableDeductibleActions } from "../../../store/ducks/financialTaxableDeductible";
// Components
import {
  Checkbox,
  Col,
  DatePicker,
  Radio,
  Row,
  Select,
  Spin,
  notification,
} from "antd";
import CustomDivider from "../../utils/customDivider";
import {
  Container,
  ExtensionButton,
  ExtensionContainer,
  Title,
} from "./styles";
import ExportExcelIcon from "../../utils/icons/export/excel";
// Services
import { findAllFinancialClassesByGroupIdAndFarmId } from "../../../services/financialClassService";
import { processReportFinancialCashFlow } from "../../../services/reportService";
import { getParameterItems } from "../../../services/generalParameterService";
import fileReader from "../../../utils/fileReader";
import { indexAllDropdownBankAccountFarm } from "../../../services/bankAccountFarmService";

const INITIAL_FORM_VALUES = {
  transactionType: "both",
  periodStartDate: null,
  periodEndDate: null,
  periodType: "currentMonth",
  intervalType: "daily",
  farmIds: null,
  financialNatureId: null,
  financialClassId: null,
  costActivityType: "AllTypes",
  showZeroValues: true,
  showAnalyticalData: false,
  financialPaymentMethodId: null,
  financialTaxableDeductibleId: null,
  bankAccountFarmId: [],
};

const validationSchema = Yup.object().shape({
  transactionType: Yup.string().required(),
  showZeroValues: Yup.boolean().required(),
  periodType: Yup.string().required(),
  periodStartDate: Yup.date().when(["periodType"], {
    is: (periodType) => periodType === "customDateRange",
    then: Yup.date().required(),
    otherwise: Yup.date().notRequired().nullable(),
  }),
  periodEndDate: Yup.date().when(["periodType"], {
    is: (periodType) => periodType === "customDateRange",
    then: Yup.date().required(),
    otherwise: Yup.date().notRequired().nullable(),
  }),
});

const LIST_COST_TYPE = [
  { costType: "AllTypes", amountAnimals: 0 },
  { costType: "Growth", amountAnimals: 0 },
  { costType: "RecreateFattenPasture", amountAnimals: 0 },
  { costType: "RecreateFattenSemiConfinement", amountAnimals: 0 },
  { costType: "RecreateFattenConfinement", amountAnimals: 0 },
  { costType: "Agriculture", amountAnimals: 0 },
  { costType: "Others", amountAnimals: 0 },
];

const LIST_PERIODS = [
  "lastSevenDays",
  "lastThirdyDays",
  "currentMonth",
  "lastMonth",
  "currentYear",
  "lastYear",
  "currentHarvest",
  "lastHarvest",
  "customDateRange",
];

const LIST_INTERVALS = ["daily", "weakly", "monthly", "yearly"];

const CODES_PARAMETERS = [4004, 4005, 4006];

const ExportReportFinancialCashFlowModal = () => {
  // Variables
  const formikRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [filteredListFinancialNature, setFilteredListFinancialNature] =
    useState([]);
  const [isLoadingFinancialNature, setIsLoadingFinancialNature] =
    useState(false);
  const [listFinancialClass, setListFinancialClass] = useState([]);
  const [isLoadingFinancialClass, setIsLoadingFinancialClass] = useState(false);
  const [bankAccountFarmOptions, setBankAccountFarmOptions] = useState([]);
  const [isLoadingBankAccountFarm, setIsLoadingBankAccountFarm] =
    useState(false);
  const [parametersValue, setParametersValue] = useState([]);
  // Redux Variables
  const {
    translation,
    groupSelected: { id: groupId, farms },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const { isModalFinancialCashFlowReportVisible: modalVisible } = useSelector(
    (state) => state.report
  );
  const {
    financialPaymentMethod: {
      loadingFinancialPaymentMethodDropdonwData,
      financialPaymentMethodDropdownData,
    },
    financialTaxableDeductible: {
      loadingFinancialTaxableDeductibleDropdonwData,
      financialTaxableDeductibleDrowpdownData,
    },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const isEnableBankAccountFarm = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4006) &&
      parametersValue.find((parameter) => parameter.code === 4006).value ===
        "true"
    );
  }, [parametersValue]);

  const isEnablePaymentMethod = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4004) &&
      parametersValue.find((parameter) => parameter.code === 4004).value ===
        "true"
    );
  }, [parametersValue]);

  const isEnableFinancialTaxableDeductible = useMemo(() => {
    return (
      parametersValue.find((parameter) => parameter.code === 4005) &&
      parametersValue.find((parameter) => parameter.code === 4005).value ===
        "true"
    );
  }, [parametersValue]);

  // Methods

  const getParameters = useCallback(async () => {
    let dataResult = [];
    try {
      for (var i = 0; i < CODES_PARAMETERS.length; i++) {
        let code = CODES_PARAMETERS[i];
        let {
          data: { results },
        } = await getParameterItems({ groupId, farmId, code });
        results.isDefault = results.farmId == null && results.groupId == null;
        results.updated = false;
        dataResult.push(results);
      }
      setParametersValue(dataResult);
    } catch (error) {
    } finally {
    }
  }, [farmId, groupId]);

  const getBankAccountFarmOptions = useCallback(async () => {
    if (isEnableBankAccountFarm && groupId && farmId) {
      setIsLoadingBankAccountFarm(true);
      try {
        const {
          data: { results },
        } = await indexAllDropdownBankAccountFarm({ groupId, farmId });
        setBankAccountFarmOptions(results || []);
      } catch (error) {
        setBankAccountFarmOptions([]);
      } finally {
        setIsLoadingBankAccountFarm(false);
      }
    }
  }, [farmId, groupId, isEnableBankAccountFarm]);

  const getFinancialClassAndNature = useCallback(async () => {
    setIsLoadingFinancialNature(true);
    setIsLoadingFinancialClass(true);
    try {
      const {
        data: { results: classes },
      } = await findAllFinancialClassesByGroupIdAndFarmId({
        groupId,
        farmId,
      });
      const natures = [
        ...new Map(
          classes
            .map((c) => c.financialNature)
            .map((item) => [item["id"], item])
        ).values(),
      ];
      setFilteredListFinancialNature(natures);
      setListFinancialClass(classes);
    } catch (error) {
    } finally {
      setIsLoadingFinancialNature(false);
      setIsLoadingFinancialClass(false);
    }
  }, [farmId, groupId]);

  function handleClose() {
    formikRef.current.resetForm(INITIAL_FORM_VALUES);
    dispatch(ReportActions.hideModal());
  }

  async function handleSubmit(values, actions) {
    if (
      values.periodStartDate &&
      values.periodEndDate &&
      moment(values.periodEndDate).diff(
        moment(values.periodStartDate),
        "months"
      ) > 12
    ) {
      formikRef.current.setFieldError(
        "periodStartDate",
        "Diff must be more than 12"
      );
      formikRef.current.setFieldError(
        "periodEndDate",
        "Diff must be more than 12"
      );

      notification.error({
        message:
          translation.disbursementReportModalRequest.messages
            .errorPeriodMoreThanTwelveMonths,
      });

      return;
    }
    const body = {
      ...values,
      farmIds: values.farmIds ?? [],
      periodStartDate: !values.periodStartDate
        ? null
        : moment(values.periodStartDate).format("YYYY-MM-DD"),
      periodEndDate: !values.periodEndDate
        ? null
        : moment(values.periodEndDate).format("YYYY-MM-DD"),
    };
    setTimeout(() => {
      setIsLoading(false);
      formikRef.current.resetForm(INITIAL_FORM_VALUES);
      dispatch(ReportActions.hideModal());
    }, 100);
    try {
      const { data } = await processReportFinancialCashFlow({
        groupId,
        farmId,
        body,
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([data]));
      let link = document.createElement("a");
      link.href = downloadUrl;
      const dayFormat = moment().format("YYYYMMDD");
      const hourFormat = moment().format("HHmm");
      link.setAttribute(
        "download",
        `Relatorio_Fluxo_de_Caixa_${dayFormat}_${hourFormat}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      notification.success({
        message: translation.successGenerateReport,
      });
    } catch (error) {
      if (error?.response) {
        try {
          const { data } = error.response;
          // Read file
          const file = await fileReader(data);

          if (typeof file === "string") {
            // Parse content and retrieve 'message'
            const result = JSON.parse(file);
            if (result.code === 5035) {
              notification.error({
                message:
                  translation.financialCashFlowReportModalRequest.messages
                    .errorNoDataFound,
              });
            } else {
              notification.error({
                message: translation.errorGenerateReport,
              });
            }
          }
        } catch (readError) {
          notification.error({
            message: translation.errorGenerateReport,
          });
        }
      } else {
        notification.error({
          message: translation.errorGenerateReport,
        });
      }
    }
  }

  useEffect(() => {
    if (modalVisible === true) {
      Promise.all([
        getParameters(),
        getBankAccountFarmOptions(),
        getFinancialClassAndNature(),
      ]);
    }

    return () => {};
  }, [
    farmId,
    getBankAccountFarmOptions,
    getFinancialClassAndNature,
    getParameters,
    groupId,
    modalVisible,
  ]);

  useEffect(() => {
    if (isEnablePaymentMethod === true)
      dispatch(
        FinancialPaymentMethodActions.getDropdownFinancialPaymentMethod(
          groupId,
          farmId
        )
      );
    if (isEnableFinancialTaxableDeductible === true)
      dispatch(
        FinancialTaxableDeductibleActions.getDropdownFinancialTaxableDeductible(
          groupId,
          farmId
        )
      );
  }, [
    dispatch,
    farmId,
    groupId,
    isEnableFinancialTaxableDeductible,
    isEnablePaymentMethod,
  ]);

  return (
    <Container
      width={750}
      visible={modalVisible}
      centered
      closable={true}
      footer={null}
      onCancel={handleClose}
    >
      <Spin spinning={isLoading}>
        <Row type="flex">
          <Col span={24}>
            <Title>
              {translation.financialCashFlowReportModalRequest.title}
            </Title>
          </Col>
        </Row>
        <Formik
          ref={formikRef}
          initialValues={INITIAL_FORM_VALUES}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          render={({
            values,
            errors,
            setFieldValue,
            submitCount,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              {Object.keys(errors).length > 0 && submitCount > 0 && (
                <Row type="flex" justify="center">
                  <label className="error">{translation.error.formError}</label>
                </Row>
              )}
              <Row type="flex" gutter={16}>
                <Col span={12}>
                  <Checkbox
                    checked={values.showZeroValues}
                    onChange={(e) =>
                      setFieldValue("showZeroValues", e.target.checked)
                    }
                  >
                    {
                      translation.financialCashFlowReportModalRequest.form
                        .showZeroValues
                    }
                  </Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox
                    checked={values.showAnalyticalData}
                    onChange={(e) =>
                      setFieldValue("showAnalyticalData", e.target.checked)
                    }
                  >
                    {
                      translation.financialCashFlowReportModalRequest.form
                        .showAnalyticalData
                    }
                  </Checkbox>
                </Col>
              </Row>
              {/* Transaction type */}
              <Row type="flex" justify="start" gutter={8}>
                <Col span={24}>
                  <Row>
                    <label
                      className={
                        errors.transactionType && submitCount > 0 ? "error" : ""
                      }
                    >
                      {
                        translation.financialCashFlowReportModalRequest.form
                          .transactionType
                      }
                    </label>
                  </Row>
                  <Row type="flex" justify="start" align="middle" gutter={16}>
                    <Radio.Group
                      style={{
                        display: "flex",
                        flexDirection: "row",
                      }}
                      name="transactionType"
                      value={values.transactionType}
                      onChange={(e) => {
                        setFieldValue("transactionType", e.target.value);
                      }}
                    >
                      <Radio value="both">Ambos</Radio>
                      <Radio value="income">Receita</Radio>
                      <Radio value="expense">Despesa</Radio>
                    </Radio.Group>
                  </Row>
                </Col>
              </Row>
              {/* Farms */}
              <Row type="flex" gutter={8}>
                <Col span={24}>
                  <Row>
                    <label>
                      {
                        translation.financialCashFlowReportModalRequest.form
                          .farmIds
                      }
                    </label>
                  </Row>
                  <Row type="flex">
                    <Select
                      mode="multiple"
                      name="farms"
                      value={
                        values.farmIds != null ? values.farmIds : undefined
                      }
                      placeholder={translation.defaultSelectPlaceholder}
                      onChange={(values) => {
                        setFieldValue("farmIds", values);
                      }}
                    >
                      {farms.length > 0 &&
                        farms.map((f) => (
                          <Select.Option key={f.id} value={f.id}>
                            {f.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Row>
                </Col>
              </Row>
              {/* Period type / custom date range/ interval type */}
              <Row type="flex" gutter={8}>
                <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
                  <Row>
                    <label className="filter-label">
                      {
                        translation.financialCashFlowReportModalRequest.form
                          .periodType
                      }
                    </label>
                  </Row>
                  <Row>
                    <Select
                      style={{ width: "100%" }}
                      name="periodType"
                      value={values.periodType || undefined}
                      placeholder={translation.defaultSelectPlaceholder}
                      onChange={(periodType) => {
                        setFieldValue("periodType", periodType);
                        if (periodType !== "customDateRange") {
                          setFieldValue("periodStartDate", null);
                          setFieldValue("periodEndDate", null);
                        }
                      }}
                    >
                      {LIST_PERIODS?.map((period) => (
                        <Select.Option key={period} value={period}>
                          {
                            translation.financialCashFlowReportModalRequest
                              .periods[period]
                          }
                        </Select.Option>
                      ))}
                    </Select>
                  </Row>
                </Col>
                {values.periodType === "customDateRange" && (
                  <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <Row type="flex" gutter={8}>
                      <Col span={12}>
                        <Row>
                          <label
                            className={
                              errors.periodStartDate && submitCount > 0
                                ? "filter-label error"
                                : "filter-label"
                            }
                          >
                            {
                              translation.financialCashFlowReportModalRequest
                                .form.periodStartDate
                            }
                            *
                          </label>
                        </Row>
                        <Row>
                          <DatePicker
                            name="periodStartDate"
                            value={values.periodStartDate || null}
                            placeholder={
                              translation.defaultDatePickerPlaceholder
                            }
                            format={"DD/MM/YYYY"}
                            onChange={(date) => {
                              setFieldValue("periodStartDate", date);
                              if (
                                values.periodEndDate &&
                                date.isAfter(values.periodEndDate)
                              )
                                setFieldValue("periodEndDate", null);
                            }}
                          />
                        </Row>
                      </Col>
                      <Col span={12}>
                        <Row>
                          <label
                            className={
                              errors.periodEndDate && submitCount > 0
                                ? "error"
                                : ""
                            }
                          >
                            {
                              translation.financialCashFlowReportModalRequest
                                .form.periodEndDate
                            }
                            *
                          </label>
                        </Row>
                        <Row>
                          <DatePicker
                            name="periodEndDate"
                            value={values.periodEndDate || null}
                            placeholder={
                              translation.defaultDatePickerPlaceholder
                            }
                            format={"DD/MM/YYYY"}
                            disabledDate={(current) =>
                              current.isAfter(moment())
                            }
                            onChange={(date) =>
                              setFieldValue("periodEndDate", date)
                            }
                          />
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                )}
                <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
                  <Row>
                    <label className="filter-label">
                      {
                        translation.financialCashFlowReportModalRequest.form
                          .intervalType
                      }
                    </label>
                  </Row>
                  <Row>
                    <Select
                      style={{ width: "100%" }}
                      name="intervalType"
                      value={values.intervalType || undefined}
                      placeholder={translation.defaultSelectPlaceholder}
                      onChange={(intervalType) => {
                        setFieldValue("intervalType", intervalType);
                      }}
                    >
                      {LIST_INTERVALS?.map((interval) => (
                        <Select.Option key={interval} value={interval}>
                          {
                            translation.financialCashFlowReportModalRequest
                              .intervals[interval]
                          }
                        </Select.Option>
                      ))}
                    </Select>
                  </Row>
                </Col>
              </Row>
              {/* Financial Nature / financial class / coast activity type */}
              <Row type="flex" gutter={8}>
                <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
                  <Row>
                    <label>
                      {
                        translation.financialCashFlowReportModalRequest.form
                          .financialNatureId
                      }
                    </label>
                  </Row>
                  <Row>
                    <Select
                      name="financialNatureId"
                      value={
                        isLoadingFinancialNature
                          ? undefined
                          : values.financialNatureId || undefined
                      }
                      placeholder={translation.defaultSelectPlaceholder}
                      loading={isLoadingFinancialNature}
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.props.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      allowClear
                      onDeselect={() => {
                        setFieldValue("financialNatureId", null);
                        setFieldValue("financialClassId", null);
                      }}
                      onChange={(value) => {
                        setFieldValue("financialNatureId", value);
                        setFieldValue("financialClassId", null);
                      }}
                    >
                      {filteredListFinancialNature.map((l) => (
                        <Select.Option key={l.id} value={l.id}>
                          {l.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Row>
                </Col>
                {/* Financial Class */}
                <Col xs={12} sm={12} md={8} lg={8} xl={8} xxl={8}>
                  <Row>
                    <label>
                      {
                        translation.financialCashFlowReportModalRequest.form
                          .financialClassId
                      }
                    </label>
                  </Row>
                  <Row>
                    <Select
                      name="financialClassId"
                      value={
                        isLoadingFinancialClass
                          ? undefined
                          : values.financialClassId || undefined
                      }
                      placeholder={translation.defaultSelectPlaceholder}
                      loading={isLoadingFinancialClass}
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.props.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      allowClear
                      onDeselect={() => {
                        setFieldValue("financialClassId", null);
                      }}
                      onChange={(value) => {
                        setFieldValue("financialClassId", value);
                      }}
                    >
                      {values.financialNatureId != null &&
                        listFinancialClass
                          .filter(
                            (l) =>
                              l.financialNature.id === values.financialNatureId
                          )
                          .map((l) => (
                            <Select.Option key={l.id} value={l.id}>
                              {l.name}
                            </Select.Option>
                          ))}
                    </Select>
                  </Row>
                </Col>
                <Col xs={12} sm={12} md={8} lg={6} xl={6} xxl={6}>
                  <Row>
                    <label className="filterLabel">
                      {
                        translation.financialCashFlowReportModalRequest.form
                          .costActivityType
                      }
                    </label>
                  </Row>
                  <Row>
                    <Select
                      style={{ width: "100%" }}
                      name="costActivityType"
                      value={values.costActivityType || undefined}
                      placeholder={translation.defaultSelectPlaceholder}
                      allowClear
                      onChange={(value) => {
                        setFieldValue("costActivityType", value);
                      }}
                    >
                      {LIST_COST_TYPE.map((l) => (
                        <Select.Option key={l.costType} value={l.costType}>
                          {`${translation.costType[l.costType]}`}
                        </Select.Option>
                      ))}
                    </Select>
                  </Row>
                </Col>
              </Row>
              {/* Payment Method / Issues / Taxable & Deductible */}
              <Row type="flex" justify="start" gutter={8}>
                {isEnablePaymentMethod && (
                  <Col xs={8} sm={8} md={8} lg={8} xl={8} xxl={8}>
                    <Row>
                      <label>
                        {
                          translation.financial.transactions.form
                            .financialPaymentMethodId
                        }
                      </label>
                    </Row>
                    <Row>
                      <Select
                        name="financialPaymentMethodId"
                        allowClear
                        loading={loadingFinancialPaymentMethodDropdonwData}
                        value={
                          financialPaymentMethodDropdownData != null &&
                          values.financialPaymentMethodId != null
                            ? financialPaymentMethodDropdownData.find(
                                (payMethod) =>
                                  payMethod.id ===
                                  values.financialPaymentMethodId
                              )
                              ? values.financialPaymentMethodId
                              : undefined
                            : undefined
                        }
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) => {
                          setFieldValue("financialPaymentMethodId", value);
                        }}
                      >
                        {financialPaymentMethodDropdownData != null &&
                          financialPaymentMethodDropdownData
                            .filter(
                              (payMethod) => payMethod.status === "Active"
                            )
                            .map((financialPaymentMethod) => (
                              <Select.Option
                                key={financialPaymentMethod.id}
                                value={financialPaymentMethod.id}
                              >
                                {financialPaymentMethod.name}
                              </Select.Option>
                            ))}
                      </Select>
                    </Row>
                  </Col>
                )}
                {isEnableFinancialTaxableDeductible && (
                  <Col xs={8} sm={8} md={8} lg={8} xl={8} xxl={8}>
                    <Row>
                      <label>
                        {
                          translation.financial.transactions.form
                            .financialTaxableDeductibleId
                        }
                      </label>
                    </Row>
                    <Row>
                      <Select
                        name="financialTaxableDeductibleId"
                        loading={loadingFinancialTaxableDeductibleDropdonwData}
                        allowClear
                        value={
                          financialTaxableDeductibleDrowpdownData != null &&
                          values.financialTaxableDeductibleId != null
                            ? financialTaxableDeductibleDrowpdownData.find(
                                (tax) =>
                                  tax.id === values.financialTaxableDeductibleId
                              )
                              ? values.financialTaxableDeductibleId
                              : undefined
                            : undefined
                        }
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                        placeholder={translation.defaultSelectPlaceholder}
                        onChange={(value) => {
                          setFieldValue("financialTaxableDeductibleId", value);
                        }}
                      >
                        {financialTaxableDeductibleDrowpdownData != null &&
                          financialTaxableDeductibleDrowpdownData
                            .filter((tax) => tax.status === "Active")
                            .map((financialTaxableDeductible) => (
                              <Select.Option
                                key={financialTaxableDeductible.id}
                                value={financialTaxableDeductible.id}
                              >
                                {financialTaxableDeductible.name}
                              </Select.Option>
                            ))}
                      </Select>
                    </Row>
                  </Col>
                )}
              </Row>
              {/* Account Farm Id */}
              {isEnableBankAccountFarm && (
                <Row type="flex" gutter={8}>
                  <Col span={8}>
                    <Row>
                      <label>
                        {
                          translation.financial.transactions.form
                            .bankAccountFarmId
                        }
                      </label>
                    </Row>
                    <Row>
                      <Select
                        name="bankAccountFarmId"
                        value={values.bankAccountFarmId || undefined}
                        placeholder={translation.defaultSelectPlaceholder}
                        loading={isLoadingBankAccountFarm}
                        mode="multiple"
                        allowClear
                        onChange={(value) =>
                          setFieldValue("bankAccountFarmId", value)
                        }
                      >
                        {bankAccountFarmOptions
                          .filter((baf) => baf.status === "Active")
                          .map((bankAccountFarm) => (
                            <Select.Option
                              key={bankAccountFarm.id}
                              value={bankAccountFarm.id}
                            >
                              {bankAccountFarm.description}
                            </Select.Option>
                          ))}
                      </Select>
                    </Row>
                  </Col>
                </Row>
              )}
              <CustomDivider dashed title="EXTENSÃO" />
              <Row type="flex">
                <Col span={24}>
                  <ExtensionContainer>
                    <ExtensionButton type="submit">
                      <ExportExcelIcon />
                    </ExtensionButton>
                  </ExtensionContainer>
                </Col>
              </Row>
            </form>
          )}
        />
      </Spin>
    </Container>
  );
};

export default ExportReportFinancialCashFlowModal;
